// ATTICUS-ANALYTICS-COMMAND-CENTER-1 — reusable Recharts wrappers.
//
// Thin, theme-consistent chart primitives for the Analytics lenses. Each chart
// accepts an optional onSegmentClick(payload) so a click can open the shared
// drilldown drawer. Colors come from the Atticus semantic palette; nothing
// here fetches data — charts are pure presentational components.
import React from "react";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, LabelList,
  AreaChart, Area, LineChart, Line, ScatterChart, Scatter, ZAxis, Treemap,
} from "recharts";
import { fmt } from "../api";

export const PALETTE = [
  "#3b6fd4", "#10b981", "#f59e0b", "#8b5cf6", "#0ea5e9",
  "#ef4444", "#14b8a6", "#f97316", "#6366f1", "#64748b",
];
export const TONE_HEX = {
  green: "#10b981", amber: "#f59e0b", red: "#ef4444", blue: "#3b6fd4", slate: "#94a3b8",
};
const AXIS = { fontSize: 11, fill: "#4a5578" };
const GRID = "#e8eaf2";

const money0 = (n) => "$" + Math.round(Number(n) || 0).toLocaleString("en-US");
const moneyK = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1e6) return "$" + (v / 1e6).toFixed(1) + "M";
  if (Math.abs(v) >= 1e3) return "$" + Math.round(v / 1e3) + "k";
  return "$" + Math.round(v);
};

function TT({ active, payload, label, money }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="an-tt">
      {label != null && <div className="an-tt-label">{label}</div>}
      {payload.map((p, i) => (
        <div key={i} className="an-tt-row">
          <span className="an-tt-dot" style={{ background: p.color || p.fill }} />
          <span className="an-tt-name">{p.name}</span>
          <span className="an-tt-val">{money ? fmt.money(p.value) : Number(p.value).toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

const H = (h) => ({ width: "100%", height: h || 260 });

// ---- Donut / Pie ----------------------------------------------------------
export function Donut({ data, nameKey = "name", valueKey = "value", height, onSegmentClick, money }) {
  return (
    <div style={H(height)}>
      <ResponsiveContainer>
        <PieChart>
          <Pie data={data} dataKey={valueKey} nameKey={nameKey} innerRadius="55%" outerRadius="80%"
               paddingAngle={1} cursor={onSegmentClick ? "pointer" : "default"}
               onClick={onSegmentClick ? (e) => e && onSegmentClick(e.payload || e) : undefined}>
            {data.map((d, i) => (
              <Cell key={i} fill={d.color || PALETTE[i % PALETTE.length]} />
            ))}
          </Pie>
          <Tooltip content={<TT money={money} />} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

// ---- Horizontal bar -------------------------------------------------------
export function HBar({ data, labelKey = "name", valueKey = "value", height, onSegmentClick, money, color }) {
  return (
    <div style={H(height)}>
      <ResponsiveContainer>
        <BarChart data={data} layout="vertical" margin={{ left: 8, right: 16, top: 4, bottom: 4 }}>
          <CartesianGrid horizontal={false} stroke={GRID} />
          <XAxis type="number" tick={AXIS} tickFormatter={money ? moneyK : undefined} />
          <YAxis type="category" dataKey={labelKey} tick={AXIS} width={130} />
          <Tooltip content={<TT money={money} />} cursor={{ fill: "rgba(59,111,212,0.06)" }} />
          <Bar dataKey={valueKey} radius={[0, 4, 4, 0]} cursor={onSegmentClick ? "pointer" : "default"}
               onClick={onSegmentClick ? (e) => e && onSegmentClick(e.payload || e) : undefined}>
            {data.map((d, i) => (
              <Cell key={i} fill={d.color || color || PALETTE[i % PALETTE.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ---- Grouped / stacked vertical bars --------------------------------------
export function GroupedBar({ data, xKey = "x", series = [], height, stacked, money, onSegmentClick }) {
  return (
    <div style={H(height)}>
      <ResponsiveContainer>
        <BarChart data={data} margin={{ left: 4, right: 8, top: 8, bottom: 4 }}>
          <CartesianGrid vertical={false} stroke={GRID} />
          <XAxis dataKey={xKey} tick={AXIS} />
          <YAxis tick={AXIS} tickFormatter={money ? moneyK : undefined} />
          <Tooltip content={<TT money={money} />} cursor={{ fill: "rgba(59,111,212,0.06)" }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {series.map((s, i) => (
            <Bar key={s.key} dataKey={s.key} name={s.label} stackId={stacked ? "a" : undefined}
                 fill={s.color || PALETTE[i % PALETTE.length]} radius={stacked ? 0 : [3, 3, 0, 0]}
                 cursor={onSegmentClick ? "pointer" : "default"}
                 onClick={onSegmentClick ? (e) => e && onSegmentClick({ ...(e.payload || e), _series: s.key }) : undefined} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ---- Trend (area or line) -------------------------------------------------
export function Trend({ data, xKey = "x", series = [], height, money, area = true, onPointClick }) {
  const Chart = area ? AreaChart : LineChart;
  return (
    <div style={H(height)}>
      <ResponsiveContainer>
        <Chart data={data} margin={{ left: 4, right: 8, top: 8, bottom: 4 }}>
          <defs>
            {series.map((s, i) => (
              <linearGradient key={s.key} id={`g-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={s.color || PALETTE[i % PALETTE.length]} stopOpacity={0.35} />
                <stop offset="100%" stopColor={s.color || PALETTE[i % PALETTE.length]} stopOpacity={0.02} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid vertical={false} stroke={GRID} />
          <XAxis dataKey={xKey} tick={AXIS} />
          <YAxis tick={AXIS} tickFormatter={money ? moneyK : undefined} />
          <Tooltip content={<TT money={money} />} />
          {series.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
          {series.map((s, i) => area ? (
            <Area key={s.key} type="monotone" dataKey={s.key} name={s.label}
                  stroke={s.color || PALETTE[i % PALETTE.length]} strokeWidth={2}
                  fill={`url(#g-${s.key})`} activeDot={{ r: 4, cursor: onPointClick ? "pointer" : "default",
                    onClick: onPointClick ? (_, e) => onPointClick(e && e.payload) : undefined }} />
          ) : (
            <Line key={s.key} type="monotone" dataKey={s.key} name={s.label}
                  stroke={s.color || PALETTE[i % PALETTE.length]} strokeWidth={2} dot={false} />
          ))}
        </Chart>
      </ResponsiveContainer>
    </div>
  );
}

// ---- Scatter --------------------------------------------------------------
export function ScatterPlot({ data, xKey = "x", yKey = "y", xLabel, yLabel, height, money, onPointClick }) {
  return (
    <div style={H(height)}>
      <ResponsiveContainer>
        <ScatterChart margin={{ left: 8, right: 12, top: 8, bottom: 16 }}>
          <CartesianGrid stroke={GRID} />
          <XAxis type="number" dataKey={xKey} name={xLabel} tick={AXIS}
                 tickFormatter={money ? moneyK : undefined} />
          <YAxis type="number" dataKey={yKey} name={yLabel} tick={AXIS}
                 tickFormatter={money ? moneyK : undefined} />
          <ZAxis range={[40, 40]} />
          <Tooltip content={<TT money={money} />} cursor={{ strokeDasharray: "3 3" }} />
          <Scatter data={data} fill="#3b6fd4" cursor={onPointClick ? "pointer" : "default"}
                   onClick={onPointClick ? (e) => e && onPointClick(e.payload || e) : undefined} />
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
}

// ---- Treemap --------------------------------------------------------------
export function TreemapChart({ data, height, onSegmentClick }) {
  return (
    <div style={H(height)}>
      <ResponsiveContainer>
        <Treemap data={data} dataKey="value" nameKey="name" stroke="#fff" aspectRatio={4 / 3}
                 content={<TreemapCell onSegmentClick={onSegmentClick} />} />
      </ResponsiveContainer>
    </div>
  );
}
function TreemapCell(props) {
  const { x, y, width, height, index, name, value, onSegmentClick, root } = props;
  const fill = PALETTE[index % PALETTE.length];
  const show = width > 52 && height > 24;
  return (
    <g cursor={onSegmentClick ? "pointer" : "default"}
       onClick={onSegmentClick ? () => onSegmentClick({ name, value, ...(props.payload || {}) }) : undefined}>
      <rect x={x} y={y} width={width} height={height} fill={fill} stroke="#fff" />
      {show && (
        <>
          <text x={x + 6} y={y + 16} fill="#fff" fontSize={11} fontWeight={600}>{name}</text>
          <text x={x + 6} y={y + 30} fill="#eaf0ff" fontSize={10}>{value}</text>
        </>
      )}
    </g>
  );
}

// ---- Bullet / gauge (simple, dependency-free) -----------------------------
// Renders a single rate (0..1) as a horizontal bullet with a target marker.
export function Bullet({ value, target, tone = "blue", label, height = 26 }) {
  const pct = Math.max(0, Math.min(1, Number(value) || 0)) * 100;
  const tpct = target != null ? Math.max(0, Math.min(1, target)) * 100 : null;
  return (
    <div className="an-bullet" style={{ height }}>
      <div className="an-bullet-track">
        <div className="an-bullet-fill" style={{ width: `${pct}%`, background: TONE_HEX[tone] }} />
        {tpct != null && <div className="an-bullet-target" style={{ left: `${tpct}%` }} />}
      </div>
      {label && <div className="an-bullet-label">{label}</div>}
    </div>
  );
}
